import {
  FDBox,
  FDContentContainer, FDPaper, FDPrimaryActionButton, FDStack,
} from '@facet-wealth/design';
import React from 'react';
import fr from 'facet-rest';
import MetaControlField from 'Components/UI/Meta/MetaForm/Components/MetaControlField';
import useBoneBackgroundColor from 'Hooks/useBoneBackgroundColor';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import { useCopyToClipboard } from 'Helpers/Utils';
import useMobileLayout from 'Hooks/useMobileLayout';
import { useSnackbarContext } from 'Context/SnackbarContext';
import useEventTrackingLogEvent, { categories, eventTypes } from 'EventTracking/useEventTrackingLogEvent';
import Divider from 'Components/UI/Divider';
import Footer from 'Components/ClientApp/Desktop/Footer';
import { buildUseStyles, useTheme } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import { dataAttrs } from 'Helpers/Testing';

const useStyles = buildUseStyles('ReferralPage', () => ({
  footer: {
    position: 'sticky',
    transform: 'initial',
    bottom: 'unset',
  },
  wrapText: {
    wordWrap: 'break-word',
  },
}));

export const Layout = () => {
  const { resource } = useMetaLayoutContext();
  const theme = useTheme();
  const isMobile = useMobileLayout();
  const classes = useStyles();
  const { copyToClipboard } = useCopyToClipboard();
  const { logStandardizedEvent } = useEventTrackingLogEvent();
  const { displayErrorMessage } = useSnackbarContext();

  useBoneBackgroundColor();

  const onCopy = async (relationship) => {
    logStandardizedEvent(categories.referralPage, eventTypes.clickButton, `Click copy ${relationship.meta('type') || 'link'}`);
    try {
      await relationship.link('related').post(); // Record that the user clicked share
    } catch (err) {
      displayErrorMessage(err, err.message);
    }
  };

  const handleClick = async (rel) => {
    // We must copy to the clipboard before we attempt to do any other async operations because
    // copying to the clipboard must happen as the direct result of user input and cannot happen
    // as the result of an async call. See: https://webkit.org/blog/10855/async-clipboard-api/
    await copyToClipboard(rel.meta('clipboardText'));
    await onCopy(rel);
  };

  return (
    <>
      <FDContentContainer maxWidth={'xl'}>
        <FDStack direction={isMobile ? 'column' : 'row'} spacing={4} paddingY={isMobile ? 4 : 8} wrap>
          <FDStack direction={'column'} spacing={3} flex={4} width={'100%'}>
            <FDStack direction={'column'} spacing={3}>
              <MetaControlField attr={'title'} {...dataAttrs('referral page title')} />
              <MetaControlField attr={'subtitle'} {...dataAttrs('referral page subtitle')} />
              <MetaControlField attr={'body'} {...dataAttrs('referral page body')} />
            </FDStack>
            <Divider color={'attention'} />
            <FDStack direction={'column'} spacing={2}>
              <MetaControlField attr={'linkTitle'} />
              <FDStack direction={isMobile ? 'column' : 'row'} spacing={3} alignItems={'flex-start'}>
                <FDPaper padding={2} flex={1} width={'100%'}>
                  <div className={classes.wrapText}>
                    <MetaControlField attr={'referralLink'} {...dataAttrs('referral link')} />
                  </div>
                </FDPaper>
                {resource.withRelationship(fr.relationships.referralLink.name, (rel) => (<FDPrimaryActionButton
                  icon={rel.meta('icon')}
                  label={rel.meta('prompt')}
                  onClick={() => handleClick(rel)}
                  {...dataAttrs('copy referral link btn')}
                />), () => null)}
              </FDStack>
            </FDStack>
            <FDStack direction={'column'} spacing={2}>
              <MetaControlField attr={'messageTitle'} />
              <FDStack direction={isMobile ? 'column' : 'row'} spacing={3} alignItems={'flex-start'}>
                <FDPaper padding={2} flex={1} width={'100%'}>
                  <div className={classes.wrapText}>
                    <MetaControlField attr={'referralMessage'} {...dataAttrs('referral message')} />
                  </div>
                </FDPaper>
                {resource.withRelationship(fr.relationships.referralMessage.name, (rel) => (<FDPrimaryActionButton
                  icon={rel.meta('icon')}
                  label={rel.meta('prompt')}
                  onClick={() => handleClick(rel)}
                  {...dataAttrs('copy referral message btn')}
                />), () => null)}
              </FDStack>
            </FDStack>
            <FDStack direction={'column'} spacing={3}>
              <MetaControlField attr={'attestation'} {...dataAttrs('referral page attestation')} />
              <MetaControlField attr={'disclosure'} {...dataAttrs('referral page disclosure')} />
            </FDStack>
          </FDStack>
          {/* This design was built with a column-based grid and we would like to have one column width of spacing between the content on the right and
           the content on the left. However we do not have a column-based grid built into our DS or layouts yet, so using this box to create a
           single column spacer for now. */}
          {!isMobile ? <FDBox width={theme.spacing(11)} /> : null}
          <FDStack flex={1} width={'100%'} minWidth={320} direction={'column'} spacing={2}>
            <MetaControlField attr={'howToInfo'} />
          </FDStack>
        </FDStack>
      </FDContentContainer>
      {!isMobile && <Footer className={classes.footer} />}
    </>
  );
};
